import { FC, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Badge, ListGroup, Popover, PopoverBody } from 'reactstrap';
import {
  COMPANY_OBJECTIVES,
  COMPANY_OBJECTIVES_TAB_OVERVIEW,
} from '../../consts/consts';
import { Me, Organization } from '../../types';
import { useConfirmApi } from '../../utils/api/ApiHooks';
import {
  OBJECTIVE_TYPE_SCOPES,
  type Objective,
  parseObjectivesForDisplay,
} from '../../utils/models/Objective';
import PersonalObjectiveItemRoot from '../Person/PersonalObjectiveItemRoot';
import { buildObjectivesOverviewSearch } from './ObjectivesOverviewPage/hooks';
import ObjectiveTypeBadge from './ObjectiveTypeBadge';
import {
  NO_OBJECTIVE_TYPES,
  type ObjectiveType,
  sortObjectiveTypesByScopeAndDescendants,
  toObjectiveTypeIdsParam,
  useObjectiveTypes,
} from './ObjectiveTypeOperations';

interface Props {
  currentOrganization: Organization;
  me: Me;
  firstDay: string;
  lastDay: string;
  isDemoOrPreviewMode?: boolean;
  header?: ReactNode;
}

interface OverviewResponse {
  results: Objective[];
  has_more_entries: boolean;
}

const NO_OBJECTIVES: Objective[] = [];

interface TypeRowProps {
  objectiveType: ObjectiveType;
  organizationObjectiveTypes: ObjectiveType[];
  objectives: Objective[];
  me: Me;
  currentOrganization: Organization;
}

const ObjectiveTypeRow: FC<TypeRowProps> = ({
  objectiveType,
  organizationObjectiveTypes,
  objectives,
  me,
  currentOrganization,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const { formatMessage } = useIntl();
  const hasObjectives = objectives.length > 0;

  return (
    <>
      <div ref={setRef} className="d-flex align-items-center mb-3">
        <div className="avatar avatar-xs me-3 flex-shrink-0">
          <div
            className="avatar-title rounded-circle"
            style={{ backgroundColor: objectiveType.color }}
          />
        </div>
        <h4
          className="fw-normal my-1 flex-grow-1 text-truncate"
          style={{ minWidth: 0 }}
        >
          <Link
            to={{
              pathname: COMPANY_OBJECTIVES_TAB_OVERVIEW(formatMessage).path,
              search: buildObjectivesOverviewSearch(
                { objectiveTypeIds: [objectiveType.id] },
                formatMessage
              ),
            }}
            target="_blank"
            rel="noopener noreferrer"
            className="text-reset"
          >
            {objectiveType.name}
          </Link>
        </h4>
        <Badge color="light" className="text-dark ms-auto flex-shrink-0">
          {objectives.length}
        </Badge>
      </div>
      {ref && (
        <Popover
          className="popover-xxl"
          trigger="hover focus"
          placement="left"
          target={ref}
          isOpen={popoverOpen}
          toggle={() => setPopoverOpen(!popoverOpen)}
        >
          <h6 className="header-pretitle d-flex align-items-center">
            <ObjectiveTypeBadge
              objectiveTypeId={objectiveType.id}
              organizationObjectiveTypes={organizationObjectiveTypes}
            />
            <span className="ms-2">
              <FormattedMessage
                id="app.views.objectives.company_panel.popover_title"
                defaultMessage="objectives"
              />
            </span>
          </h6>
          <PopoverBody>
            {popoverOpen && (
              <div
                className="mb-2"
                style={
                  hasObjectives
                    ? { width: '50vw', maxHeight: 350, overflowY: 'auto' }
                    : undefined
                }
              >
                {hasObjectives ? (
                  <ListGroup>
                    {objectives.map((objective, index) => (
                      <PersonalObjectiveItemRoot
                        key={objective.key}
                        isEditable={false}
                        isInPopover={true}
                        hideScoresAndWeight={false}
                        hideRelatedObjectives={true}
                        person={me}
                        currentOrganization={currentOrganization}
                        // @ts-expect-error — Objective vs ObjectiveWithRef
                        objective={objective}
                        showLabelHeadings={index === 0}
                        forceShowChildren={true}
                        enableParentEdit={false}
                        showDescendantsCount={false}
                        showDescendantsBreakdown={false}
                        applyFiltering={false}
                        organizationObjectiveTypes={organizationObjectiveTypes}
                        userEditableObjectiveTypes={NO_OBJECTIVE_TYPES}
                      />
                    ))}
                  </ListGroup>
                ) : (
                  <span className="text-muted small">
                    <FormattedMessage
                      id="app.views.objectives.company_panel.no_objectives"
                      defaultMessage="No objectives of this type yet."
                    />
                  </span>
                )}
              </div>
            )}
          </PopoverBody>
        </Popover>
      )}
    </>
  );
};

interface TypeSectionProps {
  objectiveType: ObjectiveType;
  organizationObjectiveTypes: ObjectiveType[];
  me: Me;
  currentOrganization: Organization;
  firstDay: string;
  lastDay: string;
  isDemoOrPreviewMode: boolean;
  onResultCount?: (typeId: number, count: number) => void;
}

const ObjectiveTypeSection: FC<TypeSectionProps> = ({
  objectiveType,
  organizationObjectiveTypes,
  me,
  currentOrganization,
  firstDay,
  lastDay,
  isDemoOrPreviewMode,
  onResultCount,
}) => {
  const params = useMemo(
    () => ({
      coverage_start_date: firstDay,
      coverage_end_date: lastDay,
      scope: 'everyone',
      objective_type_ids: toObjectiveTypeIdsParam([objectiveType.id]),
      max_depth: 0,
      page_size: 200,
    }),
    [firstDay, lastDay, objectiveType.id]
  );

  const postProcess = useMemo(
    () => (response) => {
      const results = response.results.map(parseObjectivesForDisplay);
      onResultCount?.(objectiveType.id, results.length);
      return { ...response, results };
    },
    [onResultCount, objectiveType.id]
  );

  const { data, status } = useConfirmApi<OverviewResponse>({
    method: 'POST',
    url: '/objectives/overview',
    params,
    postProcess,
    disabled: isDemoOrPreviewMode,
  });

  if (status === 'LOADING') return null;
  const objectives = data?.results ?? NO_OBJECTIVES;
  if (objectives.length === 0) return null;

  return (
    <ObjectiveTypeRow
      objectiveType={objectiveType}
      organizationObjectiveTypes={organizationObjectiveTypes}
      objectives={objectives}
      me={me}
      currentOrganization={currentOrganization}
    />
  );
};

const StrategicObjectivesPanel: FC<Props> = ({
  currentOrganization,
  me,
  firstDay,
  lastDay,
  isDemoOrPreviewMode = false,
  header,
}) => {
  const { formatMessage } = useIntl();
  const { organizationObjectiveTypes, userEditableObjectiveTypes } =
    useObjectiveTypes();

  const { linkableTypes, otherTypes } = useMemo(() => {
    const individualType = organizationObjectiveTypes.find(
      (t) => t.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
    );
    const linkableIds = new Set(individualType?.link_parent_types ?? []);
    const visibleTypes = sortObjectiveTypesByScopeAndDescendants(
      organizationObjectiveTypes.filter(
        (t) => t.scope !== OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
      )
    );
    return {
      linkableTypes: visibleTypes.filter((t) => linkableIds.has(t.id)),
      otherTypes: visibleTypes.filter((t) => !linkableIds.has(t.id)),
    };
  }, [organizationObjectiveTypes]);

  const canCreateStrategicObjective = useMemo(
    () =>
      userEditableObjectiveTypes.some(
        (t) => t.scope !== OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
      ),
    [userEditableObjectiveTypes]
  );

  const linkableResultCountsRef = useRef<Map<number, number>>(new Map());
  const [hasAnyLinkableResults, setHasAnyLinkableResults] = useState(false);

  const handleLinkableResultCount = useCallback(
    (typeId: number, count: number) => {
      linkableResultCountsRef.current.set(typeId, count);
      let total = 0;
      for (const c of linkableResultCountsRef.current.values()) total += c;
      setHasAnyLinkableResults(total > 0);
    },
    []
  );

  if (linkableTypes.length === 0 && otherTypes.length === 0) return null;

  return (
    <div className="company-objectives-panel mb-4">
      {header}
      {otherTypes.map((objectiveType) => (
        <ObjectiveTypeSection
          key={objectiveType.id}
          objectiveType={objectiveType}
          organizationObjectiveTypes={organizationObjectiveTypes}
          me={me}
          currentOrganization={currentOrganization}
          firstDay={firstDay}
          lastDay={lastDay}
          isDemoOrPreviewMode={isDemoOrPreviewMode}
        />
      ))}
      {hasAnyLinkableResults && (
        <h6 className="header-pretitle mt-4">
          <FormattedMessage
            id="app.views.objectives.company_panel.linkable_header"
            defaultMessage="Objectives that you can link to"
          />
        </h6>
      )}
      {linkableTypes.map((objectiveType) => (
        <ObjectiveTypeSection
          key={objectiveType.id}
          objectiveType={objectiveType}
          organizationObjectiveTypes={organizationObjectiveTypes}
          me={me}
          currentOrganization={currentOrganization}
          firstDay={firstDay}
          lastDay={lastDay}
          isDemoOrPreviewMode={isDemoOrPreviewMode}
          onResultCount={handleLinkableResultCount}
        />
      ))}
      {canCreateStrategicObjective && !isDemoOrPreviewMode && (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={COMPANY_OBJECTIVES(formatMessage).path}
        >
          <FormattedMessage
            id="app.views.objectives.company_panel.create_strategic_objective"
            defaultMessage="Create strategic objectives"
          />{' '}
          <i className="fe fe-external-link" />
        </a>
      )}
    </div>
  );
};

export default StrategicObjectivesPanel;
